"use client";

// ============================================================
// CLUBHUB – „Als App installieren“
// ============================================================
// Chrome/Edge/Android liefern `beforeinstallprompt`; iOS-Safari nicht —
// dort steht der Weg ueber „Teilen › Zum Home-Bildschirm“. Der Hinweis
// erscheint nur auf schmalen Schirmen, nur ausserhalb der installierten
// App und nur bis er weggeklickt wurde.
// ============================================================
import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { ClubhubMark } from "@/components/layout/clubhub-mark";

type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

const KEY = "clubhub-installhinweis-weg";

export function InstallHint() {
  const [ereignis, setEreignis] = useState<InstallEvent | null>(null);
  const [ios, setIos] = useState(false);
  const [sichtbar, setSichtbar] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
    let weg = false;
    try {
      weg = localStorage.getItem(KEY) === "1";
    } catch {}
    if (standalone || weg || window.innerWidth >= 1024) return;

    const istIos = /iPhone|iPad|iPod/.test(navigator.userAgent);
    if (istIos) {
      setIos(true);
      setSichtbar(true);
      return;
    }
    const fang = (e: Event) => {
      e.preventDefault();
      setEreignis(e as InstallEvent);
      setSichtbar(true);
    };
    window.addEventListener("beforeinstallprompt", fang);
    return () => window.removeEventListener("beforeinstallprompt", fang);
  }, []);

  function schliessen() {
    setSichtbar(false);
    try {
      localStorage.setItem(KEY, "1");
    } catch {}
  }

  async function installieren() {
    if (!ereignis) return;
    await ereignis.prompt();
    await ereignis.userChoice;
    schliessen();
  }

  if (!sichtbar) return null;
  return (
    <div className="bg-bg-elevated shadow-e3 animate-slide-up fixed inset-x-3 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] z-40 flex items-center gap-3 rounded-2xl p-3 ring-1 ring-black/[0.06] lg:hidden dark:ring-white/[0.08]">
      <ClubhubMark className="h-9 w-9" />
      <div className="min-w-0 flex-1">
        <p className="text-body-sm text-text-primary font-medium">clubhub als App</p>
        <p className="text-caption text-text-secondary">
          {ios ? (
            <>
              Tippe auf <Share className="inline h-3.5 w-3.5 align-text-bottom" aria-label="Teilen" /> und dann „Zum
              Home-Bildschirm“.
            </>
          ) : (
            "Schneller Start, Termine und Mitteilungen direkt auf dem Telefon."
          )}
        </p>
      </div>
      {!ios && (
        <button
          type="button"
          onClick={installieren}
          className="bg-accent text-accent-contrast text-body-sm flex items-center gap-1.5 rounded-lg px-3 py-2 font-medium"
        >
          <Download className="h-4 w-4" aria-hidden />
          Installieren
        </button>
      )}
      <button type="button" onClick={schliessen} aria-label="Hinweis schließen" className="text-text-tertiary grid h-8 w-8 place-items-center rounded-lg">
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
